import {
  BRIDGED_USDC_PER_CHAIN_ID,
  SOLANA_NATIVE_QUOTE_ADDRESS,
  WETH_ADDRESSES,
  WSOL_ADDRESS_SOLANA,
} from '../constants/addresses';
import { NATIVE_TOKEN_SYMBOL_PER_CHAIN } from '../constants/chain';
import type { TokenAddressKey } from './0x/token-registry.types';
import { isTokenAddressNativeAsset, isUsdStableAddress } from './multichain';
import { normalizeTokenAddress } from './normalizeAddress';

export type TokenCategory = 'usd' | 'sol' | 'btc' | 'eth';

export type CategoryResolution = {
  category: TokenCategory | undefined;
  /** Categories a failed provider lookup could still have assigned to the token. */
  unresolved: TokenCategory[];
};

/**
 * Returns the known economic asset category represented by a token address.
 * This static-map lookup only classifies USD and ETH categories; the async
 * `resolveTokenCategory` dispatcher adds provider-backed Solana resolution.
 */
export const getTokenAssetCategory = ({
  chainId,
  address,
}: TokenAddressKey): Extract<TokenCategory, 'usd' | 'eth'> | undefined => {
  const normalizedAddress = normalizeTokenAddress(address, chainId);
  const bridgedUsdcAddress = BRIDGED_USDC_PER_CHAIN_ID[chainId];
  const isBridgedUsdc =
    bridgedUsdcAddress != null &&
    normalizedAddress === normalizeTokenAddress(bridgedUsdcAddress, chainId);

  if (isUsdStableAddress(normalizedAddress, chainId) || isBridgedUsdc) return 'usd';

  const wethAddress = WETH_ADDRESSES[chainId];
  const isWeth =
    wethAddress != null && normalizedAddress === normalizeTokenAddress(wethAddress, chainId);
  const isNativeEth =
    NATIVE_TOKEN_SYMBOL_PER_CHAIN[chainId] === 'ETH' &&
    isTokenAddressNativeAsset(normalizedAddress, chainId);

  return isNativeEth || isWeth ? 'eth' : undefined;
};

export const canonicalSolanaMint = (mint: string): string =>
  mint === SOLANA_NATIVE_QUOTE_ADDRESS ? WSOL_ADDRESS_SOLANA : mint;

const possibleCategories = ({ category, unresolved }: CategoryResolution): TokenCategory[] =>
  category ? [category] : unresolved;

export const couldBeLikeKind = (sell: CategoryResolution, buy: CategoryResolution): boolean =>
  possibleCategories(sell).some((category) => possibleCategories(buy).includes(category));
